// Feature: MCRO001-F0060 (canvas rail gutter confinement, hover-edit expansion
// and comment suppression — parented to MCRO001-F0028)
// Test: T-MCRO-060 (studioaeye-choreo docs/process/tests/T-MCRO-060-rail-gutter-hover-edit.md,
// choreo MR !148 amends MCRO001-D006 §4.2 with the owner's ruling as normative)
// Tracking: studioaeye-WebUI issue #42, amended by issue #72
// (2026-09-07 follow-up ruling below).
//
// Owner ruling (screenshot-driven, 2026-08-26): "how is this in any way
// good!!" — the production build rendered demonstration timing rails as
// full-height opaque bars ploughing through section headers and movement
// text. The ruling: at rest every rail lives entirely inside the left
// gutter (the margin band where the [4x8]/[8] bar-count annotations sit);
// on hover/focus they may expand beyond it to make grabbing easy.
//
// Owner ruling (screenshot-driven, 2026-09-07 — STRIKES the 2026-08-26
// premise that the gutter is "the margin band where the [4x8]/[8] bar-count
// annotations sit"): "The rails are now showing but they need to be in
// front of the text. they fold to the edge of the text not the edge of the
// page. ... I want all the rails to sit in the margin between the edge of
// the page to the edge of the text". His screenshot names "# SECTION 1" and
// "[4x8]" themselves as things the rails were painting over — i.e. the
// bar-count chip / spanning-bracket / section-title annotations are NOT
// free space a rail may share; they are content, exactly like the movement
// prose, that a rail must never touch. The 2026-08-26 fix confined rails to
// `.mc-continuous-canvas-mirror`'s raw `padding-left` (the boundary where
// ordinary movement/allocation TEXT starts) — but MusicChoreography.css
// deliberately pulls the bar-count chip (`.mc-live-bar-chip`), the spanning
// block's duration label (`.mc-live-span-bracket b`) and the section title
// (`.mc-live-section-title`) BACK into that same padding band via a
// negative `left` offset (`-5.4rem` / `-5.5rem`), so confining to the raw
// padding value left rails sharing almost the whole band with those labels.
// A same-vintage mitigation (MusicChoreography.js's GutterGlyphHalo, an
// opaque z-index:7 mask painted over each label) hid MOST of the resulting
// collision but not all of it — see the MR screenshot evidence — and did
// nothing at all for a plain body-text row with no chip. `measureCanvasGutterPx`
// below now measures the REAL free boundary (before whichever annotation
// renders furthest left), not the raw padding value, closing that gap.
//
// This module is the ONE place that decides how many lanes fit in a given
// gutter width. It is deliberately pure (no DOM) so the containment
// invariant it guarantees can be proven algebraically and unit-tested
// without a browser: see demonstrationTimingRailGeometry.test.js. Only
// `measureCanvasGutterPx` touches the DOM, and only to read real geometry.
//
// The previous implementation anchored lanes to a hardcoded `5.35rem`
// fallback (DemonstrationTimingRail.js's `railEdgeForRoot` measured a
// `[data-timing-rail-edge]` element that no render ever produced, so the
// CSS default was silently the ONLY value ever used) and spread lanes with
// fixed 5px/26px/46px pitches regardless of how many lanes existed or how
// wide the real gutter was. With four overlapping demonstrations the fixed
// pitch alone was already enough to walk past the real text column.

// Breathing room from the very left edge of the canvas, and from the start
// of the choreography text column, so a lane's hit-box never touches either
// boundary even at the widest permitted (rest-state) size.
export const RAIL_GUTTER_EDGE_MARGIN_PX = 6;
export const RAIL_GUTTER_TEXT_MARGIN_PX = 6;

// Rest-state hit-target width ceilings. Fine pointer keeps the pre-existing
// 24px invisible hit-box around the mandated 2-3px visible line (MCRO001-D006
// §4). Coarse pointer (touch) keeps the pre-existing >=44px accessible target
// (WCAG 2.5.5) wherever the gutter has room for it, and only shrinks below
// that when packing multiple lanes into the same gutter would otherwise
// breach the text column — "degrade sanely... still confined" (owner ruling).
export const RAIL_REST_LINE_WIDTH_MAX_PX = 24;
export const RAIL_REST_LINE_WIDTH_MAX_COARSE_PX = 44;

// Owner ruling 2026-09-07 / MCRO001-D006 §4.2 amendment: "a gutter too
// narrow to show every packed line at its minimum legible width still shows
// every line, compressed, never dropped or clipped invisibly". A real
// annotation-aware gutter (see the 2026-09-07 note above) can be as narrow
// as a few pixels once a bar-count chip is present, so the rest-state line
// must have a floor it never shrinks below — it may then overlap a
// neighbouring lane (a lesser, still-legible degradation) or crowd the
// canvas's own left edge, but it is never literally 0px, and — see the
// containment construction below — it never reaches the text/annotation
// boundary regardless of this floor.
export const RAIL_REST_LINE_WIDTH_MIN_PX = 2;

// The always-on (not hover-gated) wider hit-target editors get around the
// visible line (T-MCRO-028 Case D/E discoverability fix) — capped so it can
// never itself push a lane's footprint past the gutter (see the containment
// construction below).
export const RAIL_EDITABLE_PAD_MAX_PX = 6;

// Owner ruling: hover/focus-within MAY spread lanes past the gutter. This is
// an INCREMENT added on top of the real rest-state pitch, not an independent
// absolute value — with few lanes the rest pitch alone can already exceed
// the old fixed `26px` hover offset (a wide gutter shared by only one or two
// lanes uses most of its budget even at rest), so a fixed hover target could
// end up SMALLER than rest and the "hover expands further" contract (T-MCRO-028
// Case C, the controlled Playwright suite) would silently break. Adding to
// the rest pitch instead guarantees hover always spreads further, for any lane count.
export const RAIL_HOVER_LANE_PITCH_DELTA_PX = 26;

// Used only when live layout is unavailable (no computed style, e.g. before
// first paint) — 6.5rem at the default 16px root, matching
// `.mc-continuous-canvas-mirror`'s padding-left in MusicChoreography.css.
// Real renders measure the actual (annotation-aware — see the 2026-09-07
// note above) free boundary instead of trusting this number; see
// measureCanvasGutterPx. This default is deliberately the WIDER, pre-chip
// value: it is a transient pre-layout state only (the very first render,
// before any ref is attached), self-corrects on the next real measurement
// pass (DemonstrationTimingRail.js's geometryVersion/ResizeObserver), and
// erring wide for one frame is a strictly smaller risk than erring narrow
// (which would visibly jump the rail rightward after mount).
export const RAIL_DEFAULT_GUTTER_PX = 104;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// Given the real gutter width and how many overlapping lanes must share it,
// returns the geometry every lane needs: a shared per-lane pitch and anchor
// edge, plus separate rest-state line widths for fine/coarse pointers.
//
// CONTAINMENT BY CONSTRUCTION (why this can never regress the owner's
// defect, for either the 2026-08-26 or the 2026-09-07 ruling): rather than
// growing lane positions rightward from the page edge and PROVING the
// rightmost one stays short of the text boundary (the 2026-08-26 shape —
// sound, but only as an inequality across pitch/pad/lineWidth that a future
// change to any one of them could silently invalidate), this anchors from
// the TEXT boundary itself and lays lanes out backward from it. Let
// M = RAIL_GUTTER_EDGE_MARGIN_PX, T = RAIL_GUTTER_TEXT_MARGIN_PX,
// usable = max(0, gutterPx - M - T), pitch = usable / laneCount. Whatever
// lineWidthCoarse and pad end up being (even clamped to the MIN floor above,
// even if a future change made lineWidthCoarse independent of pitch
// entirely), the LAST lane's centre is placed at exactly
//   lastCentre = (gutterPx - T) - lineWidthCoarse/2 - pad
// so its coarse hit-box right edge is, by substitution, EXACTLY
//   lastCentre + lineWidthCoarse/2 + pad = gutterPx - T < gutterPx
// — an identity, not a bound that depends on lineWidth/pad staying inside
// some other formula's assumptions. Every other lane is `pitch` further
// LEFT (`edge = lastCentre - (laneCount-1)*pitch`, lane i at
// `edge + i*pitch`), i.e. AWAY from the text and toward the page edge —
// preserving the existing "lane index grows the same direction as x"
// contract the hover-state CSS and its regression tests (T-MCRO-028 Case C)
// depend on. The fine-pointer rightmost edge (using lineWidth, which is
// <= lineWidthCoarse) is therefore always <= gutterPx - T too. A tight
// gutter (few px once annotation-aware, see measureCanvasGutterPx) crowds
// lanes toward — and, once laneCount is large enough, past — the page-edge
// margin M instead: a real but strictly less harmful degradation than
// spilling onto text, and exactly the compression the owner's ruling and
// MCRO001-D006 §4.2 call for ("compress... never dropped, never spilling
// onto the text").
export function railLaneGeometry(gutterPx, laneCount) {
  const safeGutter = Number.isFinite(gutterPx) && gutterPx > 0 ? gutterPx : RAIL_DEFAULT_GUTTER_PX;
  const count = Math.max(1, Math.trunc(laneCount) || 1);
  const usable = Math.max(0, safeGutter - RAIL_GUTTER_EDGE_MARGIN_PX - RAIL_GUTTER_TEXT_MARGIN_PX);
  const pitch = usable / count;
  const pad = Math.min(RAIL_EDITABLE_PAD_MAX_PX, pitch / 2);
  const roomForLine = Math.max(0, pitch - 2 * pad);
  const lineWidth = clamp(roomForLine, RAIL_REST_LINE_WIDTH_MIN_PX, RAIL_REST_LINE_WIDTH_MAX_PX);
  const lineWidthCoarse = clamp(roomForLine, RAIL_REST_LINE_WIDTH_MIN_PX, RAIL_REST_LINE_WIDTH_MAX_COARSE_PX);
  const hoverPitch = pitch + RAIL_HOVER_LANE_PITCH_DELTA_PX;
  // Anchor from the text boundary (see the containment note above), using
  // the WIDER of the two rest-state widths (lineWidthCoarse) so the anchor
  // is safe for whichever one a given viewport's `@media (any-pointer)`
  // rule actually renders — both share this same `edge`/`pitch` pair
  // (MusicChoreography.css's coarse-pointer block only swaps which line-
  // width variable governs `width`, never the position formula).
  const lastCentre = (safeGutter - RAIL_GUTTER_TEXT_MARGIN_PX) - lineWidthCoarse / 2 - pad;
  const edge = lastCentre - (count - 1) * pitch;
  const rightmostEdge = (width) => edge + (count - 1) * pitch + width / 2 + pad;
  return {
    gutterPx: safeGutter,
    laneCount: count,
    pitch,
    hoverPitch,
    pad,
    edge,
    lineWidth,
    lineWidthCoarse,
    rightmostEdge: rightmostEdge(lineWidth),
    rightmostEdgeCoarse: rightmostEdge(lineWidthCoarse),
  };
}

// The bar-count chip on a single-bar allocation, the spanning (multi-bar)
// block's own bracket container, and a section's own title — the same
// three element kinds GutterGlyphHalo already targets (MusicChoreography.js)
// — are the only content MusicChoreography.css deliberately renders pulled
// BACK into the mirror's padding-left band (a negative `left` offset), so
// they are the only candidates that can sit further left than the padding
// edge itself. Checked with a plain `querySelector` per selector (not
// `querySelectorAll` + a full-document reduce) — cheap, O(1) DOM lookups,
// not the per-keystroke full-document scan GutterGlyphHalo's own 2026-08-26
// performance incident (MusicChoreography.js) warns against — and used
// ONLY for EXISTENCE (present or absent), never to read a position. See
// RAIL_GUTTER_ANNOTATION_PULLBACK_PX below for why.
const RAIL_GUTTER_ANNOTATION_SELECTORS = ['.mc-live-bar-chip', '.mc-live-span-bracket', '.mc-live-section-title'];

// The three annotation kinds above pull back `-5.4rem` (bar-chip,
// span-bracket) or `-5.5rem` (section-title) — MusicChoreography.css. This
// is the WORSE (larger) of the two, at the same 16px root font-size every
// other default in this module already assumes (RAIL_DEFAULT_GUTTER_PX's
// own comment). Two CI regressions in a row (2026-09-07, WebUI issue #72
// follow-ups) — first getBoundingClientRect().left (the annotation's
// PAINTED position), then getComputedStyle(annotation).left (its DECLARED
// CSS offset, still read PER RENDER) — each measured this differently by
// a real, non-epsilon amount between a pre-join snapshot render and the
// same document once the collaboration session joins (T-MCRO-061 Case F:
// first 1px on `left`, then up to 2px, the second time WebKit-only). Both
// read SOMETHING off the live annotation element on every render; neither
// guaranteed bit-identical results between two independently-laid-out
// pages showing the same markup, on every engine. This constant reads
// NOTHING from the annotation beyond whether it exists at all — a boolean
// the two renders cannot disagree on if they show the same document — so
// there is nothing left to differ.
export const RAIL_GUTTER_ANNOTATION_PULLBACK_PX = 88; // 5.5rem * 16px

// Reads the real free-margin width straight off the live canvas.
// `.mc-continuous-canvas-mirror`'s own computed padding-left is where
// ordinary movement/allocation TEXT starts (MusicChoreography.css) — a
// single source of truth for THAT boundary, unlike the dead
// `[data-timing-rail-edge]` measurement this replaced. But (2026-09-07
// ruling, see the file header) the bar-count chip, spanning-bracket
// container and section title are deliberately rendered further left than
// that, inside the padding band itself — so the padding value alone is only
// an upper bound on the real free margin, not the margin itself. This
// subtracts the fixed RAIL_GUTTER_ANNOTATION_PULLBACK_PX whenever ANY of
// them is rendered anywhere on the canvas — an existence check, never a
// position read (see that constant's comment for why) — rather than the
// tightest one actually present; the small extra confinement this costs is
// a strictly smaller risk than a measurement that can legitimately differ
// between two renders of the identical document.
export function measureCanvasGutterPx(root) {
  const computed = typeof globalThis.getComputedStyle === 'function' && root
    ? globalThis.getComputedStyle(root)
    : null;
  const paddingLeft = Number.parseFloat(computed?.paddingLeft || '');
  if (!Number.isFinite(paddingLeft) || paddingLeft <= 0) return null;
  if (typeof root.querySelector !== 'function') return paddingLeft;
  const anyAnnotationPresent = RAIL_GUTTER_ANNOTATION_SELECTORS.some((selector) => root.querySelector(selector));
  if (!anyAnnotationPresent) return paddingLeft;
  const boundary = paddingLeft - RAIL_GUTTER_ANNOTATION_PULLBACK_PX;
  // A pullback that would push the boundary fully past the mirror's own
  // (overflow: hidden) left edge — e.g. an artificially narrowed
  // padding-left — means nothing real renders there for a rail to collide
  // with; fall back to the raw padding value exactly as if no annotation
  // were present.
  return boundary > 0 ? boundary : paddingLeft;
}

// T-MCRO-061 Case F regression, ROUND THREE (2026-09-07 CI, pipeline 8721
// job 205793, WebKit only): even after RAIL_GUTTER_ANNOTATION_PULLBACK_PX
// made every upstream measurement byte-identical across renders (this
// module's own gutterPx can no longer differ between a pre-join snapshot
// and the same document once joined), the geometry still mismatched by up
// to 2px on WebKit — the remaining source is CSS `calc()` resolving the
// FRACTIONAL pixel positions railLaneGeometry's pure division (pitch =
// usable / laneCount) can legitimately produce; a fractional device-pixel
// position is not guaranteed to snap to the same physical pixel on every
// render/engine. This integer-snaps every value before it reaches a CSS
// custom property, so `calc(edge + lane * pitch)` is thereafter EXACT
// INTEGER arithmetic for every lane on every render — nothing left for any
// engine to round differently. `railLaneGeometry` itself stays unrounded
// (its containment proof is exact real-number algebra, proven and tested
// on its own terms); rounding is a presentation-layer concern isolated
// here, at the one place raw numbers become strings a browser parses.
export function railGeometryStyle(gutterPx, laneCount) {
  const geometry = railLaneGeometry(gutterPx, laneCount);
  const pitchPx = Math.round(geometry.pitch);
  const hoverPitchPx = Math.round(geometry.hoverPitch);
  const padPx = Math.round(geometry.pad);
  const lineWidthPx = Math.round(geometry.lineWidth);
  const lineWidthCoarsePx = Math.round(geometry.lineWidthCoarse);
  // Re-anchored to hit the ROUNDED last lane's target exactly — not simply
  // Math.round(geometry.edge) independently of pitchPx — so the last
  // lane's rendered position stays pinned to the confinement boundary BY
  // CONSTRUCTION even after rounding, rather than drifting by up to
  // (laneCount-1) * 0.5px if edge and pitch were each rounded independently
  // (see demonstrationTimingRailGeometry.test.js's proof this stays bounded
  // by a flat 0.5px regardless of lane count).
  const lastCentrePx = Math.round(geometry.edge + (geometry.laneCount - 1) * geometry.pitch);
  const edgePx = lastCentrePx - (geometry.laneCount - 1) * pitchPx;
  return {
    '--mc-demo-rail-edge': `${edgePx}px`,
    '--mc-demo-lane-pitch': `${pitchPx}px`,
    '--mc-demo-lane-pitch-hover': `${hoverPitchPx}px`,
    '--mc-demo-editable-pad': `${padPx}px`,
    '--mc-demo-line-width': `${lineWidthPx}px`,
    '--mc-demo-line-width-coarse': `${lineWidthCoarsePx}px`,
  };
}

export { clamp as railClamp };
